import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useCampaigns } from "api/campaigns";
import { getBackendErrors } from "api/errors";
import { useAppSelector } from "app/hooks";
import { CustomAttributesSection } from "components/CustomAttributesSection/CustomAttributesSection";
import { FormButtons } from "components/FormControls/FormButtons";
import { getCurrentName } from "components/Layout/Achievement/AchievementImportList/utils";
import { CampaignRules } from "components/Layout/Campaign/Common/CampaignRules/CampaignRules";
import { CampaignTrigger } from "components/Layout/Campaign/Common/CampaignTriggers/CampaignTrigger";
import { formatSubmitValues } from "components/Layout/Campaign/Common/campaignHelpers";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { surveyQuery } from "constants/general";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik, FormikErrors, FormikHelpers } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate } from "react-router-dom";
import {
  selectAvaiableTranslations,
  selectDefaultLanguageData,
} from "reduxState/translations/translationsSlice";
import {
  AudienceType,
  CampaignFEValues,
  CampaignTriggers,
  CampaignTypes,
  TimeBasedCampaign,
  TriggerStrategyType,
  VisibilityTarget,
} from "types/campaigns";
import {
  backendErrors,
  parseCampaignTranslationsArrayError,
} from "utilities/backendErrors";
import { checkIfRulesEditMode } from "utilities/checkRulesEditMode";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { AutomationBasicSettings } from "./AutomationBasicSettings/AutomationBasicSettings";
import { AutomationLimits } from "./AutomationLimits/AutomationLimits";
import { AutomationStatus } from "./AutomationStatus/AutomationStatus";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    formContainer: {
      maxWidth: "872px",
      margin: `${theme.spacing(8)} auto 0`,
    },
  }),
);

interface Props {
  initialValues?: TimeBasedCampaign;
  campaignId?: string;
  isEditMode?: boolean;
  isDuplicationMode?: boolean;
  onSaveCallback?: (campaign: any) => void;
  initialErrors?: FormikErrors<CampaignFEValues>;
  onCancelCallback?: () => void;
}

export const AutomationForm = ({
  initialValues,
  isEditMode = false,
  campaignId,
  isDuplicationMode = false,
  onSaveCallback,
  initialErrors,
  onCancelCallback,
}: Props) => {
  const { message } = useMessage();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { addCampaign, editCampaign } = useCampaigns();
  const availableLanguages = useAppSelector(selectAvaiableTranslations);
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const classes = useStyles();

  const handleSubmit = async (
    values: TimeBasedCampaign,
    { setErrors, setStatus }: FormikHelpers<any>,
  ) => {
    const areRulesInEditMode = checkIfRulesEditMode();
    if (areRulesInEditMode) {
      return focusError();
    }

    const properties = formatSubmitValues({
      values,
    });

    try {
      const response =
        isEditMode && campaignId && !isDuplicationMode
          ? await editCampaign(campaignId, properties)
          : await addCampaign(properties);

      onSaveCallback
        ? onSaveCallback({
            campaignId: (response as { data: { campaignId: string } }).data
              .campaignId,
            name: defaultLang?.code
              ? getCurrentName(
                  properties?.translations as {
                    [key: string]: {
                      description?: string;
                      name: string;
                    };
                  },
                  defaultLang.code,
                )
              : "",
            ...properties,
          })
        : navigate(
            isEditMode
              ? `${links.automationsList}`
              : `${links.automationsList}${surveyQuery}`,
          );

      notify(
        message({
          id:
            isEditMode && !isDuplicationMode
              ? "automationUpdated"
              : "automationCreated",
        }),
      );
    } catch (exception) {
      const errors = backendErrors(getBackendErrors(exception));

      const parsedErrors = {
        ...errors,
        translations: parseCampaignTranslationsArrayError(
          errors?.translations,
          values?.translations,
        ),
      };

      setErrors(parsedErrors);
      setStatus(parsedErrors);
      focusError();
    }
  };

  const defaultValues: TimeBasedCampaign = {
    type: CampaignTypes.TIME,
    trigger: CampaignTriggers.TIME,
    translations: [
      defaultLang && {
        description: "",
        locale: defaultLang.code,
        name: "",
      },
    ],
    activity: {
      startsAt: null,
      endsAt: null,
    },
    rules: [],
    labels: [],
    limits: {
      executions: { interval: { type: "noLimit" } },
      executionsPerMember: { interval: { type: "noLimit" } },
      points: { interval: { type: "noLimit" } },
      pointsPerMember: { interval: { type: "noLimit" } },
    },
    active: false,
    visibility: { target: VisibilityTarget.ALL },
    triggerStrategy: {
      type: TriggerStrategyType.DAILY,
    },
    audience: {
      target: AudienceType.EVERYONE,
    },
    memberFilter: {
      strategy: "birthday",
    },
  };

  return (
    <Formik
      initialValues={initialValues || defaultValues}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize
      initialErrors={initialErrors}
    >
      {({
        handleSubmit,
        values,
        setFieldValue,
        setValues,
        errors,
        isSubmitting,
        dirty,
        initialValues,
      }) => {
        const { type, trigger, translations, limits, labels } = values;
        const triggerStrategy =
          "triggerStrategy" in values ? values.triggerStrategy : undefined;
        const audience = "audience" in values ? values.audience : undefined;

        return (
          <Form onSubmit={handleSubmit} className={classes.formContainer}>
            <CampaignTrigger
              type={type}
              trigger={trigger}
              setFieldValue={setFieldValue}
              isEditMode={isEditMode && !isDuplicationMode}
              setFieldsValue={(newValues: any) =>
                setValues({
                  ...values,
                  ...newValues,
                })
              }
              defaultLang={defaultLang}
              triggerStrategy={triggerStrategy}
              audience={audience}
              errors={errors?.trigger}
              title="selectAutomationTrigger"
            />
            <AutomationBasicSettings
              translations={translations}
              availableLanguages={availableLanguages}
            />

            <CampaignRules
              errors={errors}
              isEditMode={isEditMode && !isDuplicationMode}
              data={values}
            />
            <CustomAttributesSection labels={labels} />
            <AutomationLimits limits={limits} />
            <AutomationStatus />
            {!isSubmitting && (
              <LeaveSiteGuard
                warnBeforeRefresh
                isDirty={dirty}
                initialValues={initialValues}
                currentValues={values}
              />
            )}
            <FormButtons
              disableSubmit={isSubmitting}
              onCancelClick={() => {
                if (onCancelCallback) {
                  return onCancelCallback();
                }
                navigate(links.automationsList);
              }}
              onSubmitClick={() => handleSubmit()}
              {...(!(isEditMode && !isDuplicationMode) && {
                submitMessage: "addAutomation",
              })}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
